import { useBundle } from '@amzn/react-arb-tools';
import { BENTO_CARD_VARIANT, HEADING_TAG, ICON_NAME, useDeviceSize } from '@amzn/sss-website-theme';

import { SegmentConfigProps } from 'src/components/pages/segment/types';
import { LOWER_CARBON_FUELS_BUNDLE_NAME } from 'src/constants/panther/resource-bundles';
import { getImageRoot } from 'src/helpers';
import { t } from 'src/helpers/translations/translation-helpers';

export function useLowerCarbonFuelsConfig(): { isBundleLoading: boolean; config: SegmentConfigProps } {
  const { isSmallDesktop } = useDeviceSize();
  const imageRoot = getImageRoot(isSmallDesktop);

  const [lowerCarbonFuelsBundle, isLowerCarbonFuelsBundleLoading] = useBundle(LOWER_CARBON_FUELS_BUNDLE_NAME);

  return {
    isBundleLoading: isLowerCarbonFuelsBundleLoading,
    config: {
      heroAsset: {
        image: {
          src: `${imageRoot}/credits-segments-LCF-hero-1.jpg`,
          alt: t(lowerCarbonFuelsBundle, 'heroAsset_image_altText'),
        },
        title: t(lowerCarbonFuelsBundle, 'heroAsset_title'),
        subtitle: t(lowerCarbonFuelsBundle, 'heroAsset_subtitle'),
      },

      headingSection: {
        tagline: t(lowerCarbonFuelsBundle, 'headingSection_tagline'),
        heading: t(lowerCarbonFuelsBundle, 'headingSection_heading'),
        description: t(lowerCarbonFuelsBundle, 'headingSection_description'),
      },

      cardsDashboard: {
        ...(isSmallDesktop
          ? {
              firstCard: {
                src: `${imageRoot}/credits-segments-LCF-thechallenge-1.jpg`,
                alt: t(lowerCarbonFuelsBundle, 'cardsDashboard_firstCard_altText'),
              },
            }
          : {}),
        secondCard: {
          image: {
            src: `${imageRoot}/credits-segments-LCF-thechallenge-2.jpg`,
            alt: t(lowerCarbonFuelsBundle, 'cardsDashboard_secondCard_altText'),
          },
          title: t(lowerCarbonFuelsBundle, 'cardsDashboard_secondCard_title'),
          description: t(lowerCarbonFuelsBundle, 'cardsDashboard_secondCard_description'),
          variant: BENTO_CARD_VARIANT.STANDARD,
        },
        thirdCard: {
          title: t(lowerCarbonFuelsBundle, 'cardsDashboard_thirdCard_title'),
          description: t(lowerCarbonFuelsBundle, 'cardsDashboard_thirdCard_description'),
          link: {
            label: t(lowerCarbonFuelsBundle, 'cardsDashboard_thirdCard_link_label'),
            url: 'https://sustainabilityexchange.amazon.com/focus-areas/carbon-insets/playbook/carbon-insets/chapter-1',
            icon: ICON_NAME.CHEVRON_RIGHT,
          },
        },
        ...(isSmallDesktop
          ? {
              fourthCard: {
                src: `${imageRoot}/credits-segments-LCF-thechallenge-3.jpg`,
                alt: t(lowerCarbonFuelsBundle, 'cardsDashboard_fourthCard_altText'),
              },
            }
          : {}),
      },

      dynamicList: {
        mediaProps: {
          mediaTitle: t(lowerCarbonFuelsBundle, 'dynamicList_mediaTitle'),
          mediaTagline: t(lowerCarbonFuelsBundle, 'dynamicList_mediaTagline'),
          media: [
            {
              image: {
                src: `${imageRoot}/credits-segments-LCF-ourapproach-1.jpg`,
                alt: t(lowerCarbonFuelsBundle, 'dynamicList_firstMedia_altText'),
              },
            },
            {
              image: {
                src: `${imageRoot}/credits-segments-LCF-ourapproach-1.jpg`,
                alt: t(lowerCarbonFuelsBundle, 'dynamicList_secondMedia_altText'),
              },
            },
          ],
        },
        items: [
          {
            title: t(lowerCarbonFuelsBundle, 'dynamicList_firstItem_title'),
            body: t(lowerCarbonFuelsBundle, 'dynamicList_firstItem_body'),
          },
          {
            title: t(lowerCarbonFuelsBundle, 'dynamicList_secondItem_title'),
            body: t(lowerCarbonFuelsBundle, 'dynamicList_secondItem_body'),
          },
        ],
      },

      secondaryBentoCards: {
        firstCard: `${imageRoot}/credits-segments-LCF-ourquality-1.jpg`,
        firstCardModal: `${imageRoot}/credits-segments-LCF-modal-1.jpg`,
        secondCard: `${imageRoot}/credits-segments-LCF-ourquality-2.jpg`,
        secondCardModal: `${imageRoot}/credits-segments-LCF-modal-2.jpg`,

        bentoCardProps: {
          label: t(lowerCarbonFuelsBundle, 'secondaryBentoCards_label'),
          title: t(lowerCarbonFuelsBundle, 'secondaryBentoCards_title'),
          titleSize: HEADING_TAG.H5,
          description: t(lowerCarbonFuelsBundle, 'secondaryBentoCards_description'),
          firstCard: {
            label: t(lowerCarbonFuelsBundle, 'secondaryBentoCards_firstCard_label'),
            title: t(lowerCarbonFuelsBundle, 'secondaryBentoCards_firstCard_title'),
            image: {
              src: `${imageRoot}/credits-segments-LCF-ourquality-1.jpg`,
              alt: t(lowerCarbonFuelsBundle, 'secondaryBentoCards_firstCard_altText'),
            },
            modal: {
              label: t(lowerCarbonFuelsBundle, 'secondaryBentoCards_firstCard_label'),
              title: t(lowerCarbonFuelsBundle, 'secondaryBentoCards_firstCard_title'),
              image: {
                src: `${imageRoot}/credits-segments-LCF-modal-1.jpg`,
                alt: t(lowerCarbonFuelsBundle, 'secondaryBentoCards_firstCard_modal_altText'),
              },
              modalContent: t(lowerCarbonFuelsBundle, 'secondaryBentoCards_firstCard_modal_content'),
            },
          },
          secondCard: {
            label: t(lowerCarbonFuelsBundle, 'secondaryBentoCards_secondCard_label'),
            title: t(lowerCarbonFuelsBundle, 'secondaryBentoCards_secondCard_title'),
            image: {
              src: `${imageRoot}/credits-segments-LCF-ourquality-2.jpg`,
              alt: t(lowerCarbonFuelsBundle, 'secondaryBentoCards_secondCard_altText'),
            },
            modal: {
              image: {
                src: `${imageRoot}/credits-segments-LCF-modal-2.jpg`,
                alt: t(lowerCarbonFuelsBundle, 'secondaryBentoCards_secondCard_modal_altText'),
              },
              label: t(lowerCarbonFuelsBundle, 'secondaryBentoCards_secondCard_label'),
              title: t(lowerCarbonFuelsBundle, 'secondaryBentoCards_secondCard_title'),
              modalContent: t(lowerCarbonFuelsBundle, 'secondaryBentoCards_secondCard_modal_content'),
            },
          },
        },
      },
    },
  };
}
